import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  adminUserListSchema,
  adminUserResponseSchema,
  type AccountStatus,
  type AdminUser,
} from '@competa/shared';
import { apiRequest } from '../../api/client';
import { Badge } from '../../components/Badge';
import { Button } from '../../components/Button';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import { errorMessage } from '../../i18n/errors';
import { useAuth } from '../auth/AuthContext';
import styles from './Admin.module.css';
import { UserDialog, type UserFormValues } from './UserDialog';

/** Schlüssel der Benutzerliste im Query-Cache. */
const USERS_KEY = ['admin', 'users'] as const;

/** Farbvariante der Statusmarke je Kontozustand. */
const STATUS_TONE: Record<AccountStatus, 'success' | 'warning' | 'danger'> = {
  active: 'success',
  expired: 'warning',
  disabled: 'danger',
};

/** Zu bearbeitender Dialogzustand: geschlossen, neu oder ein bestimmter Benutzer. */
type DialogState = { mode: 'closed' } | { mode: 'create' } | { mode: 'edit'; user: AdminUser };

/**
 * Baut den Änderungskörper für `PATCH /admin/users/:id` aus den Formularwerten, ohne unveränderte
 * Felder mitzusenden (ein leeres Passwort bedeutet „unverändert").
 * @param user Der bearbeitete Benutzer.
 * @param values Formularwerte.
 * @returns Körper nur mit geänderten Feldern.
 */
export function buildPatch(user: AdminUser, values: UserFormValues): Record<string, unknown> {
  const patch: Record<string, unknown> = {};
  if (values.username !== user.username) patch.username = values.username;
  if (values.role !== user.role) patch.role = values.role;
  if (values.locale !== user.locale) patch.locale = values.locale;
  if (values.password) patch.password = values.password;
  const currentExpiry = user.expiresAt ? user.expiresAt.slice(0, 10) : '';
  if (values.expiresOn !== currentExpiry) {
    // Ablauf am Ende des gewählten Tages (UTC), damit der gewählte Tag noch voll nutzbar ist.
    patch.expiresAt = values.expiresOn ? `${values.expiresOn}T23:59:59.000Z` : null;
  }
  return patch;
}

/** Benutzerverwaltung: Liste mit Status sowie Anlegen, Bearbeiten, Sperren/Entsperren, Löschen. */
export function UsersPage() {
  const { t, i18n } = useTranslation();
  const { user: me } = useAuth();
  const queryClient = useQueryClient();
  const [dialog, setDialog] = useState<DialogState>({ mode: 'closed' });
  const [deleting, setDeleting] = useState<AdminUser | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const users = useQuery({
    queryKey: USERS_KEY,
    queryFn: () => apiRequest('GET', '/admin/users', { schema: adminUserListSchema }),
  });

  /** Lädt die Liste nach einer erfolgreichen Änderung neu. */
  const refresh = () => queryClient.invalidateQueries({ queryKey: USERS_KEY });

  const statusAction = useMutation({
    mutationFn: ({ id, action }: { id: number; action: 'deactivate' | 'reactivate' }) =>
      apiRequest('POST', `/admin/users/${id}/${action}`, { schema: adminUserResponseSchema }),
    onSuccess: refresh,
    onError: (error) => setActionError(errorMessage(t, error)),
  });

  const deleteAction = useMutation({
    mutationFn: (id: number) => apiRequest('DELETE', `/admin/users/${id}`),
    onSuccess: async () => {
      setDeleting(null);
      await refresh();
    },
    onError: (error) => {
      setDeleting(null);
      setActionError(errorMessage(t, error));
    },
  });

  /**
   * Legt einen Benutzer an oder speichert Änderungen (je nach Dialogmodus).
   * @param values Formularwerte.
   */
  async function handleSubmit(values: UserFormValues): Promise<void> {
    setNotice(null);
    setActionError(null);
    if (dialog.mode === 'edit') {
      const patch = buildPatch(dialog.user, values);
      if (Object.keys(patch).length > 0) {
        await apiRequest('PATCH', `/admin/users/${dialog.user.id}`, {
          body: patch,
          schema: adminUserResponseSchema,
        });
      }
      setNotice(t('admin.users.saved'));
    } else {
      await apiRequest('POST', '/admin/users', {
        body: {
          username: values.username,
          password: values.password,
          role: values.role,
          locale: values.locale,
        },
        schema: adminUserResponseSchema,
      });
      setNotice(t('admin.users.created', { name: values.username }));
    }
    setDialog({ mode: 'closed' });
    await refresh();
  }

  /**
   * Formatiert einen Zeitpunkt oder Platzhalter in der aktuellen Sprache.
   * @param iso ISO-Zeitpunkt oder `null`.
   * @param fallback Text für „kein Wert".
   * @returns Lokalisiertes Datum mit Uhrzeit.
   */
  const formatDateTime = (iso: string | null, fallback: string) =>
    iso ? new Date(iso).toLocaleString(i18n.language) : fallback;

  return (
    <div>
      <div className={styles.toolbar}>
        <h2>{t('admin.users.title')}</h2>
        <Button onClick={() => setDialog({ mode: 'create' })}>{t('admin.users.create')}</Button>
      </div>
      {notice && <p role="status">{notice}</p>}
      {actionError && (
        <p role="alert" className={styles.error}>
          {actionError}
        </p>
      )}
      {users.isPending && <p role="status">{t('common.loading')}</p>}
      {users.isError && (
        <p role="alert" className={styles.error}>
          {errorMessage(t, users.error)}
        </p>
      )}
      {users.data && (
        <div className={styles.tableWrap}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>{t('admin.users.name')}</th>
                <th>{t('admin.users.role')}</th>
                <th>{t('admin.users.status')}</th>
                <th>{t('admin.users.expires')}</th>
                <th>{t('admin.users.lastLogin')}</th>
                <th>{t('admin.users.actions')}</th>
              </tr>
            </thead>
            <tbody>
              {users.data.users.length === 0 && (
                <tr>
                  <td colSpan={6}>{t('admin.users.empty')}</td>
                </tr>
              )}
              {users.data.users.map((user) => {
                const isSelf = user.id === me?.id;
                return (
                  <tr key={user.id}>
                    <td>
                      <strong>{user.username}</strong> {isSelf && t('admin.users.self')}
                    </td>
                    <td>{user.role === 'admin' ? t('auth.roleAdmin') : t('auth.roleUser')}</td>
                    <td>
                      <Badge tone={STATUS_TONE[user.status]}>
                        {t(`admin.users.status_${user.status}`)}
                      </Badge>{' '}
                      {user.mustChangePassword && (
                        <Badge tone="warning">{t('admin.users.mustChange')}</Badge>
                      )}
                    </td>
                    <td>
                      {user.expiresAt
                        ? new Date(user.expiresAt).toLocaleDateString(i18n.language)
                        : t('common.unlimited')}
                    </td>
                    <td>{formatDateTime(user.lastLoginAt, t('common.never'))}</td>
                    <td>
                      <div className={styles.actions}>
                        <Button
                          variant="secondary"
                          onClick={() => setDialog({ mode: 'edit', user })}
                        >
                          {t('common.edit')}
                        </Button>
                        {!isSelf && (
                          <>
                            <Button
                              variant="secondary"
                              disabled={statusAction.isPending}
                              onClick={() => {
                                setActionError(null);
                                setNotice(null);
                                statusAction.mutate({
                                  id: user.id,
                                  action: user.active ? 'deactivate' : 'reactivate',
                                });
                              }}
                            >
                              {user.active
                                ? t('admin.users.deactivate')
                                : t('admin.users.reactivate')}
                            </Button>
                            <Button variant="secondary" onClick={() => setDeleting(user)}>
                              {t('common.delete')}
                            </Button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {dialog.mode !== 'closed' && (
        <UserDialog
          key={dialog.mode === 'edit' ? dialog.user.id : 'new'}
          user={dialog.mode === 'edit' ? dialog.user : null}
          isOpen
          onClose={() => setDialog({ mode: 'closed' })}
          onSubmit={handleSubmit}
        />
      )}
      <ConfirmDialog
        isOpen={deleting !== null}
        title={t('admin.users.deleteConfirmTitle')}
        message={t('admin.users.deleteConfirmBody', { name: deleting?.username ?? '' })}
        confirmLabel={t('common.delete')}
        busy={deleteAction.isPending}
        onConfirm={() => deleting && deleteAction.mutate(deleting.id)}
        onCancel={() => setDeleting(null)}
      />
    </div>
  );
}
